import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Tooltip, Avatar,
  Card, CardContent, Divider, Pagination
} from '@mui/material';
import {
  People, Subscriptions, ConfirmationNumber, Timeline,
  Edit, Block, LockReset, CheckCircle, Cancel,
  ArrowBack, Refresh, Add, Visibility,
  TrendingUp, CurrencyExchange, ToggleOn, ToggleOff, Campaign,
  DeleteForever, MarkEmailRead, ForwardToInbox
} from '@mui/icons-material';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
  Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/api';
import AdminEmailTab from '../components/admin/AdminEmailTab';

// ─── Constantes visuelles ─────────────────────────────────────────────────────

const ROLE_COLORS        = { admin: 'error', user: 'primary', viewer: 'default' };
const STATUS_COLORS      = { active: 'success', inactive: 'default', banned: 'error', pending: 'warning' };
const TICKET_STATUS_COLORS = { new: 'error', open: 'warning', pending: 'info', closed: 'success' };
const PRIORITY_COLORS    = { urgent: 'error', high: 'warning', medium: 'info', low: 'default' };
const SUB_STATUS_COLORS  = { active: 'success', expired: 'default', cancelled: 'error', pending: 'warning' };

const PLAN_LABELS = { monthly: 'Mensuel', annual: 'Annuel', pro: 'Pro', starter: 'Starter', team: 'Team', business: 'Business' };
const PLAN_CHART_COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#22c55e', '#ef4444', '#06b6d4'];

// ─── Composants réutilisables ─────────────────────────────────────────────────

function StatCard({ icon, label, value, color }) {
  return (
    <Card elevation={2}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: color + '.main', width: 52, height: 52 }}>{icon}</Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }} elevation={3}>
      <Typography variant="caption" fontWeight="bold">{label}</Typography>
      {payload.map((p, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color }} />
          <Typography variant="caption">{p.name} : <strong>{Number(p.value).toLocaleString()}</strong></Typography>
        </Box>
      ))}
    </Paper>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate = useNavigate();
  const [tab,     setTab]     = useState(0);
  const [stats,   setStats]   = useState(null);
  const [subStats, setSubStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert,   setAlert]   = useState({ type: '', text: '' });

  // Users
  const [users,            setUsers]           = useState([]);
  const [userTotal,        setUserTotal]        = useState(0);
  const [userPage,         setUserPage]         = useState(1);
  const [userSearch,          setUserSearch]          = useState('');
  const [userRoleFilter,      setUserRoleFilter]      = useState('');
  const [userStatusFilter,    setUserStatusFilter]    = useState('');
  const [userVerifiedFilter,  setUserVerifiedFilter]  = useState('');
  const [editUserDialog,   setEditUserDialog]   = useState(null);
  const [resetPwdDialog,   setResetPwdDialog]   = useState(null);
  const [newPassword,      setNewPassword]      = useState('');
  const [deleteUserDialog, setDeleteUserDialog] = useState(null);

  // Subscriptions
  const [subs,            setSubs]           = useState([]);
  const [subTotal,        setSubTotal]        = useState(0);
  const [subPage,         setSubPage]         = useState(1);
  const [subStatusFilter, setSubStatusFilter] = useState('');
  const [subPlanFilter,   setSubPlanFilter]   = useState('');
  const [editSubDialog,   setEditSubDialog]   = useState(null);
  const [newSubDialog,    setNewSubDialog]    = useState(false);
  const [newSub, setNewSub] = useState({ user_id: '', plan: 'monthly', start_date: '', end_date: '', amount: '', status: 'active' });

  // Tickets
  const [tickets,             setTickets]            = useState([]);
  const [ticketTotal,         setTicketTotal]         = useState(0);
  const [ticketPage,          setTicketPage]          = useState(1);
  const [ticketStatusFilter,  setTicketStatusFilter]  = useState('');
  const [ticketPriorityFilter,setTicketPriorityFilter]= useState('');
  const [viewTicket,          setViewTicket]          = useState(null);

  const showAlert = (type, text) => {
    setAlert({ type, text });
    setTimeout(() => setAlert({ type: '', text: '' }), 4000);
  };

  // ── Chargements ──
  useEffect(() => {
    adminService.getStats().then(r => setStats(r.data)).catch(console.error);
    adminService.getSubscriptionStats().then(r => setSubStats(r.data)).catch(console.error);
  }, []);

  const loadUsers = useCallback(() => {
    setLoading(true);
    adminService.getUsers({ search: userSearch, role: userRoleFilter, status: userStatusFilter, verified: userVerifiedFilter, page: userPage, limit: 15 })
      .then(r => { setUsers(r.data.users); setUserTotal(r.data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userSearch, userRoleFilter, userStatusFilter, userVerifiedFilter, userPage]);

  const loadSubs = useCallback(() => {
    setLoading(true);
    adminService.getSubscriptions({ status: subStatusFilter, plan: subPlanFilter, page: subPage, limit: 15 })
      .then(r => { setSubs(r.data.subscriptions); setSubTotal(r.data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [subStatusFilter, subPlanFilter, subPage]);

  const loadTickets = useCallback(() => {
    setLoading(true);
    adminService.getTickets({ status: ticketStatusFilter, priority: ticketPriorityFilter, page: ticketPage, limit: 15 })
      .then(r => { setTickets(r.data.tickets); setTicketTotal(r.data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [ticketStatusFilter, ticketPriorityFilter, ticketPage]);

  const refreshSubStats = () =>
    adminService.getSubscriptionStats().then(r => setSubStats(r.data)).catch(console.error);

  useEffect(() => { if (tab === 1) loadUsers();   }, [tab, loadUsers]);
  useEffect(() => { if (tab === 2) loadSubs();    }, [tab, loadSubs]);
  useEffect(() => { if (tab === 3) loadTickets(); }, [tab, loadTickets]);

  // ── Actions utilisateurs ──
  const handleUpdateUser = async () => {
    try {
      await adminService.updateUser(editUserDialog.id, { role: editUserDialog.role, status: editUserDialog.status });
      showAlert('success', 'Utilisateur mis à jour');
      setEditUserDialog(null);
      loadUsers();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) return showAlert('error', 'Minimum 6 caractères');
    try {
      await adminService.resetUserPassword(resetPwdDialog.id, newPassword);
      showAlert('success', 'Mot de passe réinitialisé');
      setResetPwdDialog(null); setNewPassword('');
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleToggleUserStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'banned' : 'active';
    try {
      await adminService.updateUser(u.id, { status: newStatus });
      showAlert('success', newStatus === 'active' ? 'Compte activé' : 'Compte désactivé');
      loadUsers();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserDialog) return;
    try {
      await adminService.deleteUser(deleteUserDialog.id);
      showAlert('success', 'Compte supprimé définitivement');
      setDeleteUserDialog(null);
      loadUsers();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleVerifyUser = async (u) => {
    try {
      await adminService.verifyUserEmail(u.id);
      showAlert('success', `Compte de ${u.full_name} marqué comme vérifié`);
      loadUsers();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleResendVerification = async (u) => {
    try {
      await adminService.resendVerification(u.id);
      showAlert('success', `Code de vérification envoyé à ${u.email}`);
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  // ── Actions abonnements ──
  const handleToggleSub = async (s) => {
    const newStatus = s.status === 'active' ? 'cancelled' : 'active';
    try {
      await adminService.updateSubscription(s.id, { status: newStatus });
      showAlert('success', newStatus === 'active' ? 'Abonnement activé' : 'Abonnement désactivé');
      loadSubs();
      refreshSubStats();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleUpdateSub = async () => {
    try {
      await adminService.updateSubscription(editSubDialog.id, { status: editSubDialog.status, end_date: editSubDialog.end_date });
      showAlert('success', 'Abonnement mis à jour');
      setEditSubDialog(null);
      loadSubs(); refreshSubStats();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const handleCreateSub = async () => {
    try {
      await adminService.createSubscription(newSub);
      showAlert('success', 'Abonnement créé');
      setNewSubDialog(false);
      setNewSub({ user_id: '', plan: 'monthly', start_date: '', end_date: '', amount: '', status: 'active' });
      loadSubs(); refreshSubStats();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  // ── Actions tickets ──
  const handleUpdateTicket = async (id, data) => {
    try {
      await adminService.updateTicket(id, data);
      showAlert('success', 'Ticket mis à jour');
      if (viewTicket?.id === id) setViewTicket(prev => ({ ...prev, ...data }));
      loadTickets();
    } catch (err) { showAlert('error', err.response?.data?.error || 'Erreur'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  // ── Données graphiques ──
  const pieData = subStats?.planDist?.map(p => ({
    name: PLAN_LABELS[p.plan] || p.plan,
    value: Number(p.count),
    revenue: Number(p.revenue),
  })) || [];

  const barData = subStats?.monthlyRevenue?.map(m => ({
    name: m.label,
    Revenus: Number(m.revenue),
    Abonnements: Number(m.count),
  })) || [];

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>Tableau de bord</Button>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Administration</Typography>
      </Box>

      {alert.text && <Alert severity={alert.type} sx={{ mb: 2 }}>{alert.text}</Alert>}

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            <Tab icon={<Timeline />}         iconPosition="start" label="Vue générale" />
            <Tab icon={<People />}           iconPosition="start" label="Utilisateurs" />
            <Tab icon={<Subscriptions />}    iconPosition="start" label="Abonnements" />
            <Tab icon={<ConfirmationNumber />} iconPosition="start" label="Tickets" />
            <Tab icon={<Campaign />}           iconPosition="start" label="Emails" />
          </Tabs>
        </Box>

        {/* ══════════════════════════════════════════
            TAB 0 — VUE GÉNÉRALE
        ══════════════════════════════════════════ */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            {!stats ? <CircularProgress /> : (
              <>
                {/* Stat cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard icon={<People />}       label="Utilisateurs"       value={stats.totalUsers}    color="primary" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard icon={<CheckCircle />}  label="Actifs"             value={stats.activeUsers}   color="success" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard icon={<Subscriptions />} label="Abonnements actifs" value={stats.activeSubscriptions} color="secondary" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard icon={<ConfirmationNumber />} label="Tickets ouverts" value={stats.openTickets} color="warning" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard icon={<Timeline />}    label="Projets"            value={stats.totalProjects} color="info" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard icon={<CurrencyExchange />} label="Revenus (FCFA)" value={Number(stats.totalRevenue || 0).toLocaleString()} color="success" />
                  </Grid>
                </Grid>

                {/* ── Graphiques abonnements ── */}
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp color="primary" /> Abonnements
                </Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {/* PieChart — répartition par plan */}
                  <Grid item xs={12} md={5}>
                    <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Répartition par plan</Typography>
                      {pieData.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Aucun abonnement</Typography>
                      ) : (
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                              {pieData.map((_, i) => <Cell key={i} fill={PLAN_CHART_COLORS[i % PLAN_CHART_COLORS.length]} />)}
                            </Pie>
                            <RTooltip formatter={(v, n, p) => [`${v} abonnés — ${Number(p.payload.revenue).toLocaleString()} FCFA`, p.payload.name]} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </Paper>
                  </Grid>

                  {/* BarChart — revenus mensuels */}
                  <Grid item xs={12} md={7}>
                    <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Revenus mensuels — 6 derniers mois (FCFA)</Typography>
                      {barData.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Aucune transaction complétée</Typography>
                      ) : (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={barData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <RTooltip content={<CustomBarTooltip />} />
                            <Legend />
                            <Bar dataKey="Revenus"      fill="#3b82f6" radius={[4,4,0,0]} />
                            <Bar dataKey="Abonnements"  fill="#22c55e" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                {/* Tables récentes */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Derniers utilisateurs</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell>Nom</TableCell><TableCell>Rôle</TableCell><TableCell>Date</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {stats.recentUsers.map(u => (
                            <TableRow key={u.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">{u.full_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                              </TableCell>
                              <TableCell><Chip label={u.role} size="small" color={ROLE_COLORS[u.role] || 'default'} /></TableCell>
                              <TableCell>{formatDate(u.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Derniers tickets</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell>Sujet</TableCell><TableCell>Priorité</TableCell><TableCell>Statut</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {stats.recentTickets.map(t => (
                            <TableRow key={t.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 180 }}>{t.subject}</Typography>
                                <Typography variant="caption" color="text.secondary">{t.author}</Typography>
                              </TableCell>
                              <TableCell><Chip label={t.priority} size="small" color={PRIORITY_COLORS[t.priority]} /></TableCell>
                              <TableCell><Chip label={t.status}   size="small" color={TICKET_STATUS_COLORS[t.status]} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        )}

        {/* ══════════════════════════════════════════
            TAB 1 — UTILISATEURS
        ══════════════════════════════════════════ */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Rechercher..." value={userSearch}
                onChange={e => { setUserSearch(e.target.value); setUserPage(1); }} sx={{ minWidth: 200 }} />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rôle</InputLabel>
                <Select value={userRoleFilter} label="Rôle" onChange={e => { setUserRoleFilter(e.target.value); setUserPage(1); }}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Statut</InputLabel>
                <Select value={userStatusFilter} label="Statut" onChange={e => { setUserStatusFilter(e.target.value); setUserPage(1); }}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="banned">Banni</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Vérification</InputLabel>
                <Select value={userVerifiedFilter} label="Vérification" onChange={e => { setUserVerifiedFilter(e.target.value); setUserPage(1); }}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="1">Vérifiés</MenuItem>
                  <MenuItem value="0">Non vérifiés</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={loadUsers}><Refresh /></IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{userTotal} utilisateur(s)</Typography>
            </Box>

            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Utilisateur</TableCell><TableCell>Rôle</TableCell>
                    <TableCell>Statut</TableCell><TableCell>Vérifié</TableCell>
                    <TableCell>Inscription</TableCell><TableCell align="right">Actions</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                              {u.full_name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{u.full_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell><Chip label={u.role}   size="small" color={ROLE_COLORS[u.role]   || 'default'} /></TableCell>
                        <TableCell><Chip label={u.status} size="small" color={STATUS_COLORS[u.status] || 'default'} /></TableCell>
                        <TableCell>{u.is_verified ? <CheckCircle color="success" fontSize="small" /> : <Cancel color="disabled" fontSize="small" />}</TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Modifier"><IconButton size="small" onClick={() => setEditUserDialog({ ...u })}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Réinitialiser mdp"><IconButton size="small" onClick={() => setResetPwdDialog(u)}><LockReset fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title={u.status === 'active' ? 'Désactiver' : 'Activer'}>
                            <IconButton size="small" color={u.status === 'active' ? 'warning' : 'success'} onClick={() => handleToggleUserStatus(u)}>
                              {u.status === 'active' ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          {!u.is_verified && (
                            <>
                              <Tooltip title="Marquer comme vérifié">
                                <IconButton size="small" color="success" onClick={() => handleVerifyUser(u)}>
                                  <MarkEmailRead fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Envoyer un code de vérification">
                                <IconButton size="small" color="info" onClick={() => handleResendVerification(u)}>
                                  <ForwardToInbox fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Supprimer définitivement">
                            <IconButton size="small" color="error" onClick={() => setDeleteUserDialog(u)}>
                              <DeleteForever fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={Math.ceil(userTotal / 15)} page={userPage} onChange={(_, p) => setUserPage(p)} />
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════
            TAB 2 — ABONNEMENTS
        ══════════════════════════════════════════ */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>

            {/* ── Graphiques ── */}
            {subStats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Total abonnements</Typography>
                    <Typography variant="h3" fontWeight="bold" color="primary.main">
                      {subStats.planDist.reduce((a, p) => a + Number(p.count), 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Actifs</Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {subStats.statusDist.find(s => s.status === 'active')?.count || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">Revenus totaux</Typography>
                    <Typography variant="h3" fontWeight="bold" color="warning.main">
                      {Number(subStats.planDist.reduce((a, p) => a + Number(p.revenue), 0)).toLocaleString()}
                      <Typography component="span" variant="body1" color="text.secondary"> FCFA</Typography>
                    </Typography>
                  </Paper>
                </Grid>

                {/* PieChart */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Répartition par plan</Typography>
                    {pieData.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Aucun abonnement</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" outerRadius={75} dataKey="value">
                            {pieData.map((_, i) => <Cell key={i} fill={PLAN_CHART_COLORS[i % PLAN_CHART_COLORS.length]} />)}
                          </Pie>
                          <RTooltip formatter={(v, n, p) => [`${v} — ${Number(p.payload.revenue).toLocaleString()} FCFA`, p.payload.name]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>

                {/* BarChart revenus */}
                <Grid item xs={12} md={7}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Revenus mensuels (FCFA)</Typography>
                    {barData.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Aucune donnée</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={barData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <RTooltip content={<CustomBarTooltip />} />
                          <Bar dataKey="Revenus" fill="#3b82f6" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* ── Filtres + actions ── */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Statut</InputLabel>
                <Select value={subStatusFilter} label="Statut" onChange={e => { setSubStatusFilter(e.target.value); setSubPage(1); }}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="expired">Expiré</MenuItem>
                  <MenuItem value="cancelled">Annulé</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Plan</InputLabel>
                <Select value={subPlanFilter} label="Plan" onChange={e => { setSubPlanFilter(e.target.value); setSubPage(1); }}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="monthly">Mensuel</MenuItem>
                  <MenuItem value="annual">Annuel</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={() => { loadSubs(); refreshSubStats(); }}><Refresh /></IconButton>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setNewSubDialog(true)} sx={{ ml: 'auto' }}>
                Nouvel abonnement
              </Button>
            </Box>

            {/* ── Tableau abonnements ── */}
            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Début</TableCell>
                    <TableCell>Fin</TableCell>
                    <TableCell>Montant</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {subs.map(s => (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{s.full_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PLAN_LABELS[s.plan] || s.plan}
                            size="small"
                            sx={{
                              bgcolor: s.plan === 'annual'  ? '#f59e0b22' : s.plan === 'monthly' ? '#3b82f622' : undefined,
                              color:   s.plan === 'annual'  ? '#f59e0b'   : s.plan === 'monthly' ? '#3b82f6'   : undefined,
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(s.start_date)}</TableCell>
                        <TableCell>{formatDate(s.end_date)}</TableCell>
                        <TableCell>{Number(s.amount).toLocaleString()} FCFA</TableCell>
                        <TableCell>
                          <Chip label={s.status} size="small" color={SUB_STATUS_COLORS[s.status] || 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          {/* Toggle activer / désactiver */}
                          <Tooltip title={s.status === 'active' ? 'Désactiver' : 'Activer'}>
                            <IconButton
                              size="small"
                              color={s.status === 'active' ? 'error' : 'success'}
                              onClick={() => handleToggleSub(s)}
                            >
                              {s.status === 'active'
                                ? <ToggleOff fontSize="small" />
                                : <ToggleOn fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => setEditSubDialog({ ...s })}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!subs.length && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Aucun abonnement
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={Math.ceil(subTotal / 15)} page={subPage} onChange={(_, p) => setSubPage(p)} />
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════
            TAB 3 — TICKETS
        ══════════════════════════════════════════ */}
        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Statut</InputLabel>
                <Select value={ticketStatusFilter} label="Statut" onChange={e => { setTicketStatusFilter(e.target.value); setTicketPage(1); }}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="new">Nouveau</MenuItem>
                  <MenuItem value="open">Ouvert</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="closed">Fermé</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Priorité</InputLabel>
                <Select value={ticketPriorityFilter} label="Priorité" onChange={e => { setTicketPriorityFilter(e.target.value); setTicketPage(1); }}>
                  <MenuItem value="">Toutes</MenuItem>
                  <MenuItem value="urgent">Urgente</MenuItem>
                  <MenuItem value="high">Haute</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="low">Basse</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={loadTickets}><Refresh /></IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{ticketTotal} ticket(s)</Typography>
            </Box>

            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>#</TableCell><TableCell>Sujet</TableCell>
                    <TableCell>Auteur</TableCell><TableCell>Département</TableCell>
                    <TableCell>Priorité</TableCell><TableCell>Statut</TableCell>
                    <TableCell>Date</TableCell><TableCell align="right">Actions</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {tickets.map(t => (
                      <TableRow key={t.id} hover>
                        <TableCell>#{t.id}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" noWrap>{t.subject}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2">{t.author}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.author_email}</Typography>
                        </TableCell>
                        <TableCell>{t.department}</TableCell>
                        <TableCell><Chip label={t.priority} size="small" color={PRIORITY_COLORS[t.priority]} /></TableCell>
                        <TableCell><Chip label={t.status}   size="small" color={TICKET_STATUS_COLORS[t.status]} /></TableCell>
                        <TableCell>{formatDate(t.created_at)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir"><IconButton size="small" onClick={() => setViewTicket(t)}><Visibility fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Fermer">
                            <IconButton size="small" color="success" onClick={() => handleUpdateTicket(t.id, { status: 'closed' })}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!tickets.length && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aucun ticket</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={Math.ceil(ticketTotal / 15)} page={ticketPage} onChange={(_, p) => setTicketPage(p)} />
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════
            TAB 4 — EMAILS MARKETING
        ══════════════════════════════════════════ */}
        {tab === 4 && <AdminEmailTab />}

      </Paper>

      {/* ══════════════════════════════════════════
          DIALOGS
      ══════════════════════════════════════════ */}

      {/* Modifier utilisateur */}
      <Dialog open={!!editUserDialog} onClose={() => setEditUserDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Modifier l'utilisateur</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">{editUserDialog?.email}</Typography>
          <FormControl fullWidth>
            <InputLabel>Rôle</InputLabel>
            <Select value={editUserDialog?.role || ''} label="Rôle"
              onChange={e => setEditUserDialog(prev => ({ ...prev, role: e.target.value }))}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Statut</InputLabel>
            <Select value={editUserDialog?.status || ''} label="Statut"
              onChange={e => setEditUserDialog(prev => ({ ...prev, status: e.target.value }))}>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="banned">Banni</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateUser}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Réinitialiser mot de passe */}
      <Dialog open={!!resetPwdDialog} onClose={() => { setResetPwdDialog(null); setNewPassword(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{resetPwdDialog?.email}</Typography>
          <TextField fullWidth type="password" label="Nouveau mot de passe" value={newPassword}
            onChange={e => setNewPassword(e.target.value)} helperText="Minimum 6 caractères" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetPwdDialog(null); setNewPassword(''); }}>Annuler</Button>
          <Button variant="contained" color="warning" onClick={handleResetPassword}>Réinitialiser</Button>
        </DialogActions>
      </Dialog>

      {/* Supprimer utilisateur */}
      <Dialog open={!!deleteUserDialog} onClose={() => setDeleteUserDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Supprimer définitivement</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>Cette action est irréversible. Toutes les données liées seront supprimées.</Alert>
          <Typography variant="body2">
            Êtes-vous sûr de vouloir supprimer le compte de <strong>{deleteUserDialog?.full_name}</strong> ({deleteUserDialog?.email}) ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserDialog(null)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser} startIcon={<DeleteForever />}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Modifier abonnement */}
      <Dialog open={!!editSubDialog} onClose={() => setEditSubDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Modifier l'abonnement</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {editSubDialog?.full_name} — {PLAN_LABELS[editSubDialog?.plan] || editSubDialog?.plan}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Plan</InputLabel>
            <Select value={editSubDialog?.plan || ''} label="Plan"
              onChange={e => setEditSubDialog(prev => ({ ...prev, plan: e.target.value }))}>
              <MenuItem value="monthly">Mensuel (5 950 FCFA)</MenuItem>
              <MenuItem value="annual">Annuel (59 500 FCFA)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Statut</InputLabel>
            <Select value={editSubDialog?.status || ''} label="Statut"
              onChange={e => setEditSubDialog(prev => ({ ...prev, status: e.target.value }))}>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="expired">Expiré</MenuItem>
              <MenuItem value="cancelled">Annulé</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth type="date" label="Date de fin" InputLabelProps={{ shrink: true }}
            value={editSubDialog?.end_date?.split('T')[0] || ''}
            onChange={e => setEditSubDialog(prev => ({ ...prev, end_date: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSubDialog(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateSub}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Nouvel abonnement */}
      <Dialog open={newSubDialog} onClose={() => setNewSubDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel abonnement</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField fullWidth label="ID Utilisateur" type="number" value={newSub.user_id}
            onChange={e => setNewSub(p => ({ ...p, user_id: e.target.value }))} />
          <FormControl fullWidth>
            <InputLabel>Plan</InputLabel>
            <Select value={newSub.plan} label="Plan" onChange={e => setNewSub(p => ({ ...p, plan: e.target.value, amount: e.target.value === 'annual' ? '59500' : '5950' }))}>
              <MenuItem value="monthly">Mensuel — 5 950 FCFA / mois</MenuItem>
              <MenuItem value="annual">Annuel — 59 500 FCFA / an</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth type="date" label="Date début" InputLabelProps={{ shrink: true }}
            value={newSub.start_date} onChange={e => setNewSub(p => ({ ...p, start_date: e.target.value }))} />
          <TextField fullWidth type="date" label="Date fin" InputLabelProps={{ shrink: true }}
            value={newSub.end_date} onChange={e => setNewSub(p => ({ ...p, end_date: e.target.value }))} />
          <TextField fullWidth label="Montant (FCFA)" type="number" value={newSub.amount}
            onChange={e => setNewSub(p => ({ ...p, amount: e.target.value }))} />
          <FormControl fullWidth>
            <InputLabel>Statut</InputLabel>
            <Select value={newSub.status} label="Statut" onChange={e => setNewSub(p => ({ ...p, status: e.target.value }))}>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewSubDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateSub}>Créer</Button>
        </DialogActions>
      </Dialog>

      {/* Voir ticket */}
      <Dialog open={!!viewTicket} onClose={() => setViewTicket(null)} maxWidth="md" fullWidth>
        <DialogTitle>Ticket #{viewTicket?.id} — {viewTicket?.subject}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={viewTicket?.priority}   size="small" color={PRIORITY_COLORS[viewTicket?.priority]} />
            <Chip label={viewTicket?.status}     size="small" color={TICKET_STATUS_COLORS[viewTicket?.status]} />
            <Chip label={viewTicket?.department} size="small" variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            De : {viewTicket?.author} ({viewTicket?.author_email}) — {formatDate(viewTicket?.created_at)}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{viewTicket?.message}</Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight="bold">Changer statut :</Typography>
            {['open', 'pending', 'closed'].map(s => (
              <Chip key={s} label={s} size="small" clickable
                color={viewTicket?.status === s ? TICKET_STATUS_COLORS[s] : 'default'}
                onClick={() => { handleUpdateTicket(viewTicket.id, { status: s }); setViewTicket(p => ({ ...p, status: s })); }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTicket(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
